import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AirplanemodeActiveOutlinedIcon from "@mui/icons-material/AirplanemodeActiveOutlined";
import AssignmentTurnedInOutlinedIcon from "@mui/icons-material/AssignmentTurnedInOutlined";
import AutoFixHighOutlinedIcon from "@mui/icons-material/AutoFixHighOutlined";
import ContentCutOutlinedIcon from "@mui/icons-material/ContentCutOutlined";
import Diversity3OutlinedIcon from "@mui/icons-material/Diversity3Outlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import SearchOutlinedIcon from "@mui/icons-material/SearchOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import AddIcon from "@mui/icons-material/Add";
import dayjs from "dayjs";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import {
  approveSpecialService,
  completeSpecialService,
  createSpecialService,
  getSpecialServices,
  startSpecialServiceTransport,
  submitSpecialService,
  updateSpecialService,
} from "../../../services/dashboard";
import { useSessionStore } from "../../../store/useSessionStore";
import { formatR } from "../../../utils/money";
import {
  OperationsEmptyState,
  OperationsPageShell,
  OperationsSectionHeader,
  ifasColors,
} from "../ifas/components/OperationsUI";
import {
  StandardMetricCard,
  STANDARD_TONES,
  StatusDotChip,
  StandardCountBadge,
  StandardPager,
  standardTableHeadCellSx,
  standardTableRowSx,
  standardPrimaryBtnSx,
  standardGhostBtnSx,
  standardOutlineBtnSx,
} from "../ifas/components/StandardUI";

type ServiceType = "REPATRIATION" | "EXPATRIATION" | "EXHUMATION" | "CREMATION" | "EMBALMING";

type SpecialServiceRecord = Record<string, any> & {
  serviceNumber?: string;
  serviceType?: ServiceType;
  status?: string;
};

const serviceTypes: Array<{
  key: ServiceType;
  title: string;
  description: string;
  icon: ReactNode;
  handoff: string;
}> = [
  {
    key: "REPATRIATION",
    title: "Repatriation",
    description: "Domestic or international movement with documents, fleet notice, cost and receiving mortuary control.",
    icon: <LocalShippingOutlinedIcon />,
    handoff: "Fleet Centre + Receiving Mortuary",
  },
  {
    key: "EXPATRIATION",
    title: "Expatriation",
    description: "Cross-border import/export with permits, embassy contact, embalming and OK-to-send readiness.",
    icon: <AirplanemodeActiveOutlinedIcon />,
    handoff: "Operations + Finance + Embassy",
  },
  {
    key: "EXHUMATION",
    title: "Exhumation",
    description: "Consent-led exhumation with COGTA, SAPS, cemetery closure and direct reburial rules.",
    icon: <ContentCutOutlinedIcon />,
    handoff: "Operations + SAPS",
  },
  {
    key: "CREMATION",
    title: "Cremation",
    description: "Cremation document pack, Schedule A-E, pacemaker check, supervisor sign-off and fee tracking.",
    icon: <AutoFixHighOutlinedIcon />,
    handoff: "Mortuary + Finance + Undertaker",
  },
  {
    key: "EMBALMING",
    title: "Embalming",
    description: "Partial/full embalming with death registration, family permission, infectious flag and certificate.",
    icon: <Diversity3OutlinedIcon />,
    handoff: "Mortuary + Undertaker",
  },
];

const statusOptions = ["DRAFT", "DOCUMENTS_PENDING", "READY_FOR_APPROVAL", "APPROVED", "IN_PROGRESS", "COMPLETED", "CANCELLED"];
const directionOptions = ["DOMESTIC", "INTERNATIONAL", "INCOMING_TO_SA", "OUTGOING_FROM_SA", "NOT_APPLICABLE"];
const embalmingTypes = ["PARTIAL_48H", "FULL_168H", "FULL_POWDER_EXTERNAL_PM", "FULL_POWDER_FULL_PM", "HEAD_ONLY_PM"];

const requiredDocsByType: Record<ServiceType, Array<{ key: string; label: string }>> = {
  REPATRIATION: [
    { key: "dha1663Received", label: "DHA1663 received" },
    { key: "deathCertificateReceived", label: "Death certificate received" },
    { key: "burialOrderReceived", label: "Burial order received" },
    { key: "claimApproved", label: "Scheme claim approved" },
    { key: "paymentConfirmed", label: "Cash payment confirmed" },
    { key: "onlineLinkRecorded", label: "Icebolethu online link recorded" },
    { key: "bodyTransferFormCompleted", label: "Annexure D body transfer form completed" },
  ],
  EXPATRIATION: [
    { key: "deathCertificateReceived", label: "Death certificate" },
    { key: "burialOrderReceived", label: "Burial order" },
    { key: "idOrPassportReceived", label: "ID / passport" },
    { key: "familyLetterReceived", label: "Family letter" },
    { key: "embassyLetterReceived", label: "Embassy letter" },
    { key: "certificateOfComplianceReceived", label: "Certificate of compliance" },
    { key: "embalmingCertificateReceived", label: "Embalming certificate" },
    { key: "nonInfectiousCertificateReceived", label: "Non-infectious certificate" },
    { key: "importExportPermitReceived", label: "Import/export permit" },
    { key: "embassyContacted", label: "Embassy contacted" },
    { key: "translationCertificatesReceived", label: "Certified translations when required" },
    { key: "okToSendReceived", label: "Air freight OK to Send when applicable" },
  ],
  EXHUMATION: [
    { key: "cogtaLetterReceived", label: "COGTA / Disaster letter" },
    { key: "tribalAuthorityOriginReceived", label: "Tribal Authority origin letter" },
    { key: "tribalAuthorityDestinationReceived", label: "Tribal Authority destination letter" },
    { key: "environmentalHealthLetterReceived", label: "Environmental Health letter" },
    { key: "cemeteryAdminLetterReceived", label: "Cemetery Administrator letter" },
    { key: "sapsLetterReceived", label: "SAPS letter" },
    { key: "familyAffidavitsReceived", label: "Both family affidavits" },
    { key: "provincialCogtaApprovalReceived", label: "Provincial COGTA approval" },
    { key: "sapsOfficerPresent", label: "SAPS officer present" },
    { key: "cemeteryClosedConfirmed", label: "Cemetery closed" },
    { key: "undertakerSupervised", label: "Undertaker supervised" },
    { key: "originalCoffinRetained", label: "Original coffin retained" },
    { key: "noViewingConfirmed", label: "No viewing confirmed" },
    { key: "directReburialConfirmed", label: "Direct reburial confirmed" },
  ],
  CREMATION: [
    { key: "burialOrderReceived", label: "Burial order" },
    { key: "deathCertificateReceived", label: "Death certificate" },
    { key: "cremationDocumentsReceived", label: "Cremation documents" },
    { key: "intermentFormReceived", label: "Interment form" },
    { key: "scheduleAReceived", label: "Schedule A" },
    { key: "scheduleBReceived", label: "Schedule B" },
    { key: "scheduleCReceived", label: "Schedule C" },
    { key: "scheduleDReceived", label: "Schedule D" },
    { key: "scheduleEReceived", label: "Schedule E" },
    { key: "pacemakerRemoved", label: "Pacemaker removed / N/A" },
    { key: "bodyPrepSupervisorSignoff", label: "Body prep supervisor sign-off" },
    { key: "crematoriumFeePaid", label: "Crematorium fee paid" },
  ],
  EMBALMING: [
    { key: "deathRegistered", label: "Death registered" },
    { key: "familyPermission", label: "Family permission captured" },
    { key: "embalmingCertificateReceived", label: "Certificate uploaded/received" },
  ],
};

const defaultForm: SpecialServiceRecord = {
  serviceType: "REPATRIATION",
  status: "DRAFT",
  direction: "DOMESTIC",
  ratePerKm: 9.5,
};

const formatLabel = (value?: string) =>
  String(value || "Not captured")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const toDateTimeInput = (value?: string) => (value ? dayjs(value).format("YYYY-MM-DDTHH:mm") : "");
const toDateInput = (value?: string) => (value ? dayjs(value).format("YYYY-MM-DD") : "");

/**
 * Context column: a route/summary line with a supporting detail beneath,
 * derived from whichever fields the service type actually populates.
 */
const contextLines = (record: SpecialServiceRecord): { primary: string; secondary: string } => {
  const route = (from?: string, to?: string) => [from, to].filter(Boolean).join(" → ");
  const crossBorder = (direction?: string) =>
    direction && !["DOMESTIC", "NOT_APPLICABLE"].includes(String(direction)) ? formatLabel(direction) : "";

  switch (record.serviceType) {
    case "EXPATRIATION":
      return {
        primary: route(record.countryOfDeath, record.countryOfBurial) || "Not captured",
        secondary: record.okToSendReceived
          ? "OK-to-send: received"
          : record.permitReceivedAt
            ? "Permit received"
            : "OK-to-send: pending",
      };
    case "EMBALMING":
      return {
        primary: record.embalmingType ? formatLabel(record.embalmingType) : "Not captured",
        secondary: record.certificateNumber
          ? "Certificate issued"
          : record.infectiousStatus
            ? `Infectious: ${formatLabel(record.infectiousStatus)}`
            : "",
      };
    case "CREMATION":
      return {
        primary: record.originLocation || record.destinationLocation || "Not captured",
        secondary: record.bodyPrepSupervisorSignoff
          ? "Supervisor signed"
          : record.pacemakerRemoved
            ? "Pacemaker cleared"
            : "",
      };
    default:
      return {
        primary: route(record.originLocation, record.destinationLocation) || record.reason || "Not captured",
        secondary: record.receivingMortuary
          ? `Receiving: ${record.receivingMortuary}`
          : crossBorder(record.direction),
      };
  }
};

function Field({
  label,
  value,
  onChange,
  select,
  options = [],
  type,
  multiline,
}: {
  label: string;
  value: any;
  onChange: (value: any) => void;
  select?: boolean;
  options?: string[];
  type?: string;
  multiline?: boolean;
}) {
  return (
    <TextField
      fullWidth
      label={label}
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value)}
      select={select}
      type={type}
      multiline={multiline}
      minRows={multiline ? 3 : undefined}
      InputLabelProps={type === "date" || type === "datetime-local" ? { shrink: true } : undefined}
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: 2,
          bgcolor: ifasColors.surface,
        },
      }}
    >
      {select &&
        options.map((option) => (
          <MenuItem key={option} value={option}>
            {formatLabel(option)}
          </MenuItem>
        ))}
    </TextField>
  );
}

function FormSection({ title, subtitle, children }: { title: string; subtitle?: string; children: ReactNode }) {
  return (
    <Paper elevation={0} sx={{ p: 2, border: `1px solid ${ifasColors.border}`, borderRadius: 2 }}>
      <OperationsSectionHeader title={title} meta={subtitle} />
      {children}
    </Paper>
  );
}

function InfoCard({ title, value }: { title: string; value?: ReactNode }) {
  return (
    <Paper elevation={0} sx={{ p: 1.5, border: `1px solid ${ifasColors.border}`, borderRadius: 2, minHeight: 72 }}>
      <Typography sx={{ fontSize: 11, fontWeight: 900, color: ifasColors.orange, textTransform: "uppercase" }}>{title}</Typography>
      <Typography sx={{ mt: 0.5, fontWeight: 900, color: ifasColors.text }}>{value || "Not captured"}</Typography>
    </Paper>
  );
}

export default function SpecialServicesPortal() {
  const { userName } = useSessionStore();
  const [serviceType, setServiceType] = useState<ServiceType>("REPATRIATION");
  const [status, setStatus] = useState("");
  const [searchText, setSearchText] = useState("");
  const [records, setRecords] = useState<SpecialServiceRecord[]>([]);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [selected, setSelected] = useState<SpecialServiceRecord | null>(null);
  const [formState, setFormState] = useState<SpecialServiceRecord>({ ...defaultForm });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(0);
  useEffect(() => setPage(0), [serviceType, status, searchText]);

  const activeConfig = useMemo(() => serviceTypes.find((item) => item.key === serviceType) || serviceTypes[0], [serviceType]);

  const loadServices = useCallback(async () => {
    setIsLoading(true);
    const response = await getSpecialServices({
      serviceType,
      status: status || null,
      searchText: searchText || null,
      page: 0,
      size: 20,
    });
    if (response?.status === 200) {
      setRecords(response.data?.services || []);
      setStatusCounts(response.data?.statusCounts || {});
    } else {
      toast.error(response?.data?.message || "Failed to load special services");
    }
    setIsLoading(false);
  }, [serviceType, status, searchText]);

  useEffect(() => {
    loadServices();
  }, [loadServices]);

  const updateField = (key: string, value: any) => {
    setFormState((prev) => ({ ...prev, [key]: value }));
  };

  const openNew = () => {
    setSelected(null);
    setFormState({ ...defaultForm, serviceType, requestedBy: userName || "" });
    setDialogOpen(true);
  };

  const openEdit = (record: SpecialServiceRecord) => {
    setSelected(record);
    setFormState({ ...defaultForm, ...record });
    setDialogOpen(true);
  };

  const saveDraft = async () => {
    setIsLoading(true);
    const payload = buildPayload(formState);
    const response = selected?.serviceNumber
      ? await updateSpecialService(selected.serviceNumber, payload)
      : await createSpecialService(payload);
    if (response?.status === 200) {
      toast.success("Special service saved");
      setFormState(response.data || formState);
      setSelected(response.data || selected);
      setDialogOpen(false);
      await loadServices();
    } else {
      toast.error(response?.data?.message || "Unable to save special service");
    }
    setIsLoading(false);
  };

  const transition = async (action: "submit" | "approve" | "start" | "complete") => {
    const serviceNumber = formState.serviceNumber || selected?.serviceNumber;
    if (!serviceNumber) {
      toast.info("Save the service first, then submit it.");
      return;
    }
    setIsLoading(true);
    const payload = buildPayload(formState);
    const response =
      action === "submit"
        ? await submitSpecialService(serviceNumber, payload)
        : action === "approve"
          ? await approveSpecialService(serviceNumber, payload)
          : action === "start"
            ? await startSpecialServiceTransport(serviceNumber, { requestedBy: userName || formState.requestedBy, actionAt: new Date().toISOString() })
          : await completeSpecialService(serviceNumber, payload);
    if (response?.status === 200) {
      toast.success(action === "submit" ? "Submitted for approval" : action === "approve" ? "Approved" : action === "start" ? "Transport started" : "Completed");
      setFormState(response.data || formState);
      setSelected(response.data || selected);
      await loadServices();
    } else {
      toast.error(response?.data?.message || `Unable to ${action} service`);
    }
    setIsLoading(false);
  };

  const checklistCompletion = useMemo(() => {
    const docs = requiredDocsByType[formState.serviceType || serviceType] || [];
    if (!docs.length) return 0;
    const complete = docs.filter((doc) => Boolean(formState[doc.key])).length;
    return Math.round((complete / docs.length) * 100);
  }, [formState, serviceType]);

  return (
    <OperationsPageShell
      eyebrow="Special Services"
      title="Special Services Control Centre"
      description="Run repatriation, expatriation, exhumation, cremation and embalming as controlled workflows with documents, approvals and role handoffs."
      actions={
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" startIcon={<RefreshOutlinedIcon />} onClick={loadServices} sx={standardGhostBtnSx}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNew} sx={standardPrimaryBtnSx}>
            New Service
          </Button>
        </Stack>
      }
    >
      {isLoading && <Loader isLoading={isLoading} />}
      <Stack spacing={2}>
        <Box sx={{ display: "grid", gridTemplateColumns: { md: "repeat(4, 1fr)" }, gap: 1.5 }}>
          <StandardMetricCard label="Draft / Pending" value={(statusCounts.DRAFT || 0) + (statusCounts.DOCUMENTS_PENDING || 0)} caption="Being prepared" tone="orange" icon={<AssignmentTurnedInOutlinedIcon />} />
          <StandardMetricCard label="Ready for approval" value={statusCounts.READY_FOR_APPROVAL || 0} caption="Awaiting sign-off" tone="blue" icon={<AssignmentTurnedInOutlinedIcon />} />
          <StandardMetricCard label="Approved / Active" value={(statusCounts.APPROVED || 0) + (statusCounts.IN_PROGRESS || 0)} caption="In execution" tone="purple" icon={<AssignmentTurnedInOutlinedIcon />} />
          <StandardMetricCard label="Completed" value={statusCounts.COMPLETED || 0} caption="This period" tone="green" icon={<AssignmentTurnedInOutlinedIcon />} />
        </Box>

        <Paper elevation={0} sx={{ p: 0.75, border: `1px solid ${ifasColors.border}`, borderRadius: "14px", boxShadow: "0 4px 14px rgba(20,24,31,.05)" }}>
          <Tabs
            value={serviceType}
            onChange={(_, value) => {
              setServiceType(value);
              setStatus("");
            }}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 44,
              "& .MuiTabs-indicator": { display: "none" },
              "& .MuiTab-root": { minHeight: 40, fontWeight: 800, textTransform: "none", borderRadius: "10px", minWidth: "auto", px: 2, mx: 0.25, color: ifasColors.muted },
              "& .Mui-selected": { bgcolor: ifasColors.softOrange, color: `${ifasColors.deepOrange} !important` },
            }}
          >
            {serviceTypes.map((item) => (
              <Tab key={item.key} value={item.key} label={item.title} icon={item.icon as any} iconPosition="start" />
            ))}
          </Tabs>
        </Paper>

        <Paper elevation={0} sx={{ p: 2.25, border: `1px solid ${ifasColors.border}`, borderRadius: "16px", background: `linear-gradient(135deg, ${alpha(ifasColors.orange, 0.09)}, ${ifasColors.surface} 46%)` }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "flex-start" }} justifyContent="space-between">
            <Stack direction="row" spacing={1.5} alignItems="flex-start">
              <Box sx={{ width: 44, height: 44, borderRadius: "12px", bgcolor: ifasColors.softOrange, color: ifasColors.orange, display: "grid", placeItems: "center", flexShrink: 0 }}>
                {activeConfig.icon}
              </Box>
              <Box>
                <Typography sx={{ fontSize: 20, fontWeight: 900, color: ifasColors.text }}>{activeConfig.title}</Typography>
                <Typography sx={{ mt: 0.4, color: ifasColors.muted, fontSize: 13.5, lineHeight: 1.45, maxWidth: 520 }}>{activeConfig.description}</Typography>
              </Box>
            </Stack>
            <Chip label={activeConfig.handoff} size="small" sx={{ bgcolor: STANDARD_TONES.grey.bg, color: STANDARD_TONES.grey.color, fontWeight: 800, borderRadius: "8px", height: 28 }} />
          </Stack>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mt: 2 }}>
              <TextField
                size="small"
                placeholder="Search case, funeral file, deceased"
                value={searchText}
                onChange={(event) => setSearchText(event.target.value)}
                InputProps={{ startAdornment: <SearchOutlinedIcon sx={{ mr: 1, color: ifasColors.muted }} /> }}
                sx={{ minWidth: 280, bgcolor: ifasColors.surface, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              />
              <TextField
                size="small"
                select
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                SelectProps={{ displayEmpty: true }}
                sx={{ minWidth: 190, bgcolor: ifasColors.surface, "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
              >
                <MenuItem value="">All statuses</MenuItem>
                {statusOptions.map((item) => (
                  <MenuItem key={item} value={item}>
                    {formatLabel(item)}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>
        </Paper>

        <Paper elevation={0} sx={{ border: `1px solid ${ifasColors.border}`, borderRadius: "16px", overflow: "hidden", bgcolor: ifasColors.surface, boxShadow: "0 4px 14px rgba(20,24,31,.05)" }}>
          <Box sx={{ px: 2.5, py: 2.25, borderBottom: `1px solid ${ifasColors.border}`, display: "flex", alignItems: "center", gap: 1.25 }}>
            <Typography sx={{ fontSize: 17, fontWeight: 800, color: ifasColors.text }}>{activeConfig.title} services</Typography>
            <StandardCountBadge count={records.length} />
          </Box>
          <Box
            sx={{
              overflowX: "auto",
              scrollbarWidth: "thin",
              scrollbarColor: `${ifasColors.border} transparent`,
              "&::-webkit-scrollbar": { height: 6 },
              "&::-webkit-scrollbar-track": { background: "transparent" },
              "&::-webkit-scrollbar-thumb": { background: ifasColors.border, borderRadius: 8 },
              "&::-webkit-scrollbar-thumb:hover": { background: ifasColors.muted },
            }}
          >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={standardTableHeadCellSx}>Service</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Case / Funeral File</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Deceased</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Context</TableCell>
                <TableCell sx={standardTableHeadCellSx}>Status</TableCell>
                <TableCell align="right" sx={standardTableHeadCellSx} />
              </TableRow>
            </TableHead>
            <TableBody>
              {records.slice(page * 10, page * 10 + 10).map((record) => (
                <TableRow key={record.serviceNumber} hover sx={standardTableRowSx}>
                  <TableCell>
                    <Typography sx={{ fontSize: 14, fontWeight: 900, color: ifasColors.text }}>{record.serviceNumber}</Typography>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: ifasColors.muted, mt: 0.3 }}>{formatLabel(record.serviceType)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 14, fontWeight: 900, color: ifasColors.text }}>{record.caseNumber || "No case linked"}</Typography>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: ifasColors.muted, mt: 0.3 }}>{record.funeralFileNumber || "No funeral file"}</Typography>
                  </TableCell>
                  <TableCell sx={{ fontSize: 14, fontWeight: 600, color: ifasColors.text }}>{record.deceasedFullName || "Not captured"}</TableCell>
                  <TableCell>
                    <Typography sx={{ fontSize: 14, fontWeight: 900, color: ifasColors.text }}>{contextLines(record).primary}</Typography>
                    {contextLines(record).secondary && (
                      <Typography sx={{ fontSize: 11.5, fontWeight: 600, color: ifasColors.muted, mt: 0.3 }}>{contextLines(record).secondary}</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <StatusDotChip status={record.status} />
                  </TableCell>
                  <TableCell align="right">
                    <Button onClick={() => openEdit(record)} variant="outlined" sx={{ ...standardOutlineBtnSx, minHeight: 34, px: 1.6 }}>
                      Open
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {!records.length && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <OperationsEmptyState title="No special services yet" description="Create the first service record when a case needs one of these workflows." />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          </Box>
          <StandardPager page={page} total={records.length} onPage={setPage} />
        </Paper>
      </Stack>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} justifyContent="space-between" alignItems={{ md: "center" }}>
            <Box>
              <Typography sx={{ fontSize: 22, fontWeight: 900 }}>
                {formState.serviceNumber || `New ${formatLabel(formState.serviceType)}`}
              </Typography>
              <Typography sx={{ color: ifasColors.muted }}>{formatLabel(formState.serviceType)} workflow and approval checklist</Typography>
            </Box>
            <Stack direction="row" spacing={1}>
              <Chip
                label={`${checklistCompletion}% checklist`}
                sx={{
                  fontWeight: 900,
                  borderRadius: "8px",
                  bgcolor: checklistCompletion === 100 ? STANDARD_TONES.green.bg : STANDARD_TONES.amber.bg,
                  color: checklistCompletion === 100 ? STANDARD_TONES.green.color : STANDARD_TONES.amber.color,
                }}
              />
              <StatusDotChip status={formState.status} />
            </Stack>
          </Stack>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: ifasColors.page }}>
          <Stack spacing={2}>
            <Box sx={{ display: "grid", gridTemplateColumns: { md: "repeat(4, 1fr)" }, gap: 1.5 }}>
              <InfoCard title="Type" value={formatLabel(formState.serviceType)} />
              <InfoCard title="Calculated Cost" value={formState.calculatedCost ? formatR(formState.calculatedCost) : "Auto after distance"} />
              <InfoCard title="Approved By" value={formState.approvedBy} />
              <InfoCard title="Completed By" value={formState.completedBy} />
              <InfoCard title="Fleet Assignment" value={formState.assignedVehicleRegistration || formState.assignedDriverName ? `${formState.assignedVehicleRegistration || "Vehicle"} / ${formState.assignedDriverName || "Driver"}` : "Use Fleet Portal assignment"} />
            </Box>
            <WorkflowTypeSection formState={formState} updateField={updateField} />
            <CaseContextSection formState={formState} updateField={updateField} />
            <ServiceSpecificSection formState={formState} updateField={updateField} />
            <ChecklistSection formState={formState} updateField={updateField} completion={checklistCompletion} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
          <Button onClick={() => setDialogOpen(false)} sx={standardGhostBtnSx}>
            Close
          </Button>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" onClick={saveDraft} sx={standardGhostBtnSx}>
              Save Draft
            </Button>
            <Button variant="contained" onClick={() => transition("submit")} sx={standardPrimaryBtnSx}>
              Submit
            </Button>
            <Button variant="contained" color="success" onClick={() => transition("approve")} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 900 }}>
              Approve
            </Button>
            {formState.serviceType === "REPATRIATION" && (
              <Button variant="contained" color="info" onClick={() => transition("start")} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 900 }}>
                Start Transport
              </Button>
            )}
            <Button variant="contained" color="secondary" onClick={() => transition("complete")} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 900 }}>
              Complete
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </OperationsPageShell>
  );
}

function WorkflowTypeSection({ formState, updateField }: { formState: SpecialServiceRecord; updateField: (key: string, value: any) => void }) {
  return (
    <FormSection title="Workflow Type" subtitle="Changing the type changes the required fields and backend validation rules.">
      <Box sx={{ display: "grid", gridTemplateColumns: { md: "repeat(2, 1fr)" }, gap: 2 }}>
        <Field label="Service Type" select options={serviceTypes.map((item) => item.key)} value={formState.serviceType} onChange={(value) => updateField("serviceType", value)} />
        <Field label="Direction" select options={directionOptions} value={formState.direction} onChange={(value) => updateField("direction", value)} />
      </Box>
    </FormSection>
  );
}

function CaseContextSection({ formState, updateField }: { formState: SpecialServiceRecord; updateField: (key: string, value: any) => void }) {
  return (
    <FormSection title="Case Context" subtitle="Link this service to the funeral file, case and body movement record.">
      <Box sx={{ display: "grid", gridTemplateColumns: { md: "repeat(3, 1fr)" }, gap: 2 }}>
        <Field label="Case Number" value={formState.caseNumber} onChange={(value) => updateField("caseNumber", value)} />
        <Field label="Funeral File Number" value={formState.funeralFileNumber} onChange={(value) => updateField("funeralFileNumber", value)} />
        <Field label="Body Removal Number" value={formState.bodyRemovalNumber} onChange={(value) => updateField("bodyRemovalNumber", value)} />
        <Field label="Deceased Full Name" value={formState.deceasedFullName} onChange={(value) => updateField("deceasedFullName", value)} />
        <Field label="Requested By" value={formState.requestedBy} onChange={(value) => updateField("requestedBy", value)} />
        <Field label="Assigned To" value={formState.assignedTo} onChange={(value) => updateField("assignedTo", value)} />
      </Box>
    </FormSection>
  );
}

function ServiceSpecificSection({ formState, updateField }: { formState: SpecialServiceRecord; updateField: (key: string, value: any) => void }) {
  const type = formState.serviceType as ServiceType;
  if (type === "REPATRIATION") {
    return (
      <FormSection title="Repatriation Movement" subtitle="Route, fee calculation and 24-hour fleet handoff.">
        <Stack spacing={2}>
          <Paper elevation={0} sx={{ p: 1.5, border: `1px solid ${ifasColors.border}`, borderRadius: 2, bgcolor: ifasColors.surface }}>
            <Typography sx={{ fontSize: 12, fontWeight: 900, color: ifasColors.muted }}>
              Driver and vehicle assignment is done from Fleet Management using the existing case assignment modal. Once the case is assigned, the active repatriation record is updated automatically.
            </Typography>
          </Paper>
          <Box sx={{ display: "grid", gridTemplateColumns: { md: "repeat(3, 1fr)" }, gap: 2 }}>
            <Field label="Repatriation Type" select options={["DOMESTIC", "INTERNATIONAL"]} value={formState.direction} onChange={(value) => updateField("direction", value)} />
            <Field label="Origin" value={formState.originLocation} onChange={(value) => updateField("originLocation", value)} />
            <Field label="Destination" value={formState.destinationLocation} onChange={(value) => updateField("destinationLocation", value)} />
            <Field label="Distance KM" type="number" value={formState.distanceKm} onChange={(value) => updateField("distanceKm", value)} />
            <Field label="Rate per KM" type="number" value={formState.ratePerKm} onChange={(value) => updateField("ratePerKm", value)} />
            <Field label="Fleet Notice Date/Time" type="datetime-local" value={toDateTimeInput(formState.fleetNoticeAt)} onChange={(value) => updateField("fleetNoticeAt", value)} />
            <Field label="Client Type" select options={["SCHEME", "CASH", "TOP_UP"]} value={formState.clientType} onChange={(value) => updateField("clientType", value)} />
            <Field label="Receiving Mortuary" value={formState.receivingMortuary} onChange={(value) => updateField("receivingMortuary", value)} />
            <Field label="Fleet Request Details" value={formState.fleetRequestDetails} onChange={(value) => updateField("fleetRequestDetails", value)} multiline />
            <Field label="Online Link Reference" value={formState.onlineLinkReference} onChange={(value) => updateField("onlineLinkReference", value)} />
            <Field label="Annexure D Reference" value={formState.bodyTransferFormReference} onChange={(value) => updateField("bodyTransferFormReference", value)} />
            <Field label="Assigned Driver" value={formState.assignedDriverName} onChange={(value) => updateField("assignedDriverName", value)} />
            <Field label="Assigned Vehicle" value={formState.assignedVehicleRegistration || formState.assignedVehicleNumber} onChange={(value) => updateField("assignedVehicleRegistration", value)} />
            <Field label="Transport Started At" type="datetime-local" value={toDateTimeInput(formState.transportStartedAt)} onChange={(value) => updateField("transportStartedAt", value)} />
            <Field label="Transport Completed At" type="datetime-local" value={toDateTimeInput(formState.transportCompletedAt)} onChange={(value) => updateField("transportCompletedAt", value)} />
          </Box>
        </Stack>
      </FormSection>
    );
  }
  if (type === "EXPATRIATION") {
    return (
      <FormSection title="Expatriation Control" subtitle="Cross-border permits, embassy controls and OK-to-send readiness.">
        <Box sx={{ display: "grid", gridTemplateColumns: { md: "repeat(3, 1fr)" }, gap: 2 }}>
          <Field label="Direction" select options={["INCOMING_TO_SA", "OUTGOING_FROM_SA"]} value={formState.direction} onChange={(value) => updateField("direction", value)} />
          <Field label="Country of Death" value={formState.countryOfDeath} onChange={(value) => updateField("countryOfDeath", value)} />
          <Field label="Country of Burial" value={formState.countryOfBurial} onChange={(value) => updateField("countryOfBurial", value)} />
          <Field label="Body Status" value={formState.bodyStatus} onChange={(value) => updateField("bodyStatus", value)} />
          <Field label="Embassy Contact Assigned To" value={formState.embassyContactAssignedTo} onChange={(value) => updateField("embassyContactAssignedTo", value)} />
          <Field label="Permit Applied At" type="datetime-local" value={toDateTimeInput(formState.importExportPermitAppliedAt)} onChange={(value) => updateField("importExportPermitAppliedAt", value)} />
          <Field label="Permit Received At" type="datetime-local" value={toDateTimeInput(formState.importExportPermitReceivedAt)} onChange={(value) => updateField("importExportPermitReceivedAt", value)} />
          <Field label="Air Freight Details" value={formState.fleetRequestDetails} onChange={(value) => updateField("fleetRequestDetails", value)} />
          <Field label="Embassy Correspondence" value={formState.embassyCorrespondence} onChange={(value) => updateField("embassyCorrespondence", value)} multiline />
          <Field label="Document Checklist Notes" value={formState.documentChecklistJson} onChange={(value) => updateField("documentChecklistJson", value)} multiline />
          <Field label="Notes" value={formState.notes} onChange={(value) => updateField("notes", value)} multiline />
        </Box>
      </FormSection>
    );
  }
  if (type === "EXHUMATION") {
    return (
      <FormSection title="Exhumation Controls" subtitle="Consent, SAPS, cemetery closure and direct reburial rules.">
        <Box sx={{ display: "grid", gridTemplateColumns: { md: "repeat(3, 1fr)" }, gap: 2 }}>
          <Field label="Reason for Exhumation" value={formState.reason} onChange={(value) => updateField("reason", value)} />
          <Field label="Original Burial Details" value={formState.originalBurialDetails} onChange={(value) => updateField("originalBurialDetails", value)} />
          <Field label="Reburial Location" value={formState.reburialLocation} onChange={(value) => updateField("reburialLocation", value)} />
          <Field label="Notes" value={formState.notes} onChange={(value) => updateField("notes", value)} multiline />
        </Box>
      </FormSection>
    );
  }
  if (type === "CREMATION") {
    return (
      <FormSection title="Cremation Preparation" subtitle="Schedule A-E, body preparation and fee controls.">
        <Box sx={{ display: "grid", gridTemplateColumns: { md: "repeat(3, 1fr)" }, gap: 2 }}>
          <Field label="Body Status" value={formState.bodyStatus} onChange={(value) => updateField("bodyStatus", value)} />
          <Field label="Notes" value={formState.notes} onChange={(value) => updateField("notes", value)} />
          <Field label="Document Checklist Notes" value={formState.documentChecklistJson} onChange={(value) => updateField("documentChecklistJson", value)} />
        </Box>
      </FormSection>
    );
  }
  return (
    <FormSection title="Embalming Procedure" subtitle="Classification, family consent, infectious status and certificate capture.">
      <Box sx={{ display: "grid", gridTemplateColumns: { md: "repeat(3, 1fr)" }, gap: 2 }}>
        <Field label="Embalming Type" select options={embalmingTypes} value={formState.embalmingType} onChange={(value) => updateField("embalmingType", value)} />
        <Field label="Date of Death" type="date" value={toDateInput(formState.dateOfDeath)} onChange={(value) => updateField("dateOfDeath", value)} />
        <Field label="Cause of Death" value={formState.causeOfDeath} onChange={(value) => updateField("causeOfDeath", value)} />
        <Field label="Infectious Status" value={formState.infectiousStatus} onChange={(value) => updateField("infectiousStatus", value)} />
        <Field label="Certificate Number" value={formState.certificateNumber} onChange={(value) => updateField("certificateNumber", value)} />
        <Field label="Procedure Notes" value={formState.procedureNotes} onChange={(value) => updateField("procedureNotes", value)} multiline />
      </Box>
    </FormSection>
  );
}

function ChecklistSection({
  formState,
  updateField,
  completion,
}: {
  formState: SpecialServiceRecord;
  updateField: (key: string, value: any) => void;
  completion: number;
}) {
  const docs = requiredDocsByType[(formState.serviceType || "REPATRIATION") as ServiceType] || [];
  return (
    <FormSection title="Required Checklist" subtitle={`${completion}% complete. Submit uses backend validation from the SOP/FRD rules.`}>
      <Box sx={{ display: "grid", gridTemplateColumns: { sm: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" }, gap: 0.5 }}>
        {docs.map((doc) => (
          <FormControlLabel
            key={doc.key}
            control={
              <Checkbox
                checked={Boolean(formState[doc.key])}
                onChange={(event) => updateField(doc.key, event.target.checked)}
                sx={{ color: ifasColors.orange, "&.Mui-checked": { color: ifasColors.orange } }}
              />
            }
            label={<Typography sx={{ fontSize: 13, fontWeight: 700 }}>{doc.label}</Typography>}
          />
        ))}
      </Box>
    </FormSection>
  );
}

function buildPayload(formState: SpecialServiceRecord) {
  const payload: Record<string, any> = { ...formState };
  ["distanceKm", "ratePerKm"].forEach((key) => {
    if (payload[key] === "") payload[key] = null;
    if (payload[key] != null) payload[key] = Number(payload[key]);
  });
  ["fleetNoticeAt", "dateOfDeath", "importExportPermitAppliedAt", "importExportPermitReceivedAt", "transportStartedAt", "transportCompletedAt"].forEach((key) => {
    if (payload[key] === "") payload[key] = null;
  });
  return payload;
}
